import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ShortTextIcon from "@mui/icons-material/ShortText";
import SubjectIcon from "@mui/icons-material/Subject";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { Editor } from "@tiptap/react"; // Import Editor type
import { useEffect, useRef, useState } from "react";
import showdown from "showdown";
import { EditPencil } from "src/icons/edit-pencil";
import { Refresh } from "src/icons/refresh";
import { useDispatch } from "src/store";
interface SelectedTextItem {
  text: string;
  element: string;
  html: string;
  from: number;
  to: number;
}
interface RegenerateDropdownProps {
  startCord: number;
  endCord: number;
  bottomCord: number;
  rightCord: number;
  open: boolean;
  selectedText: SelectedTextItem[];
  editor: Editor | null; // Add editor to props
  onClose: () => void;
  setMarginBottom: (marginBottom: number) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>; // Add scrollContainerRef
}

// Utility function to strip HTML tags
const stripHtml = (html: string): string => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

export const RegenerateDropdown: React.FC<RegenerateDropdownProps> = ({
  startCord,
  endCord,
  bottomCord,
  rightCord,
  open,
  selectedText,
  editor, // Destructure editor from props
  onClose = () => {},
  setMarginBottom,
  scrollContainerRef,
}) => {
  // console.log("open   in regenerateDropdown", open);

  // console.log("selectedText", selectedText);
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(open);
  const [responeDialog, setResponeDialog] = useState(false);
  const [regenerateText, setRegenerateText] = useState([
    {
      from: 0,
      to: 0,
      html: "",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [followUpPrompt, setFollowUpPrompt] = useState("");
  const [marginTop, setMarginTop] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const getCloudFrontUrl = () => {
    if (
      process.env.NEXT_PUBLIC_BRANCH === "dev" ||
      process.env.NODE_ENV === "development"
    ) {
      return "https://py-staging.seowind.io/regenerate_article/regenerate";
    }
    if (process.env.NEXT_PUBLIC_BRANCH === "staging") {
      return "https://py-staging.seowind.io/regenerate_article/regenerate";
    }
    return "https://py.seowind.io/regenerate_article/regenerate";
  };
  useEffect(() => {
    //close dropdown when click outside

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        // console.log("clicked outside");
        setIsOpen(false);
        onClose();
        setMarginBottom(0);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    setResponeDialog(false);
  }, []);

  useEffect(() => {
    const marginTop = selectedText[selectedText.length - 1].to;

    setMarginTop(marginTop);
  }, [selectedText]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  const allOptions = {
    Rewrite: {
      icon: (
        <EditPencil
          sx={{
            fontSize: "1.4rem",
            color: "#121828 !important",
            mt: "3.5px",
            mr: "0.2rem",
          }}
        />
      ),
      label: "Rewrite",
    },
    Expand: {
      icon: (
        <SubjectIcon
          sx={{
            fontSize: "1.4rem",
            color: "#121828 !important",
            // mt: "3.5px",
            mr: "0.2rem",
          }}
        />
      ),
      label: "Expand",
    },
    Shorten: {
      icon: (
        <ShortTextIcon
          sx={{
            fontSize: "1.4rem",
            color: "#121828 !important",
            // mt: "1px",
            mr: "0.2rem",
          }}
        />
      ),
      label: "Shorten",
    },
    Humanize: {
      icon: (
        <PsychologyIcon
          sx={{
            fontSize: "1.4rem",
            color: "#121828 !important",
            // mt: "1px",
            mr: "0.2rem",
          }}
        ></PsychologyIcon>
      ),
      label: "Humanize",
    },
  };
  // console.log("isOpen", isOpen);
  const handleClick = async (option: string) => {
    // console.log("clicked option", option);

    let html = "";
    let from = selectedText[0].from;
    let to = selectedText[selectedText.length - 1].to;
    // if selectedtest length is greater then 300 words tell user to select less text

    selectedText.forEach((text, index) => {
      html += text.html + "\n\n";
    });

    setSelectedOption(option);
    // Handle stream response
    setLoading(true);
    setResponeDialog(true);
    setIsOpen(false);
    setRegenerateText([{ from: from, to: to, html: "" }]);
    let accumulatedText = "";
    let prompt = customPrompt ? customPrompt : null;
    if (option === "Custom" && followUpPrompt) {
      prompt = followUpPrompt;
      html = regenerateText[regenerateText.length - 1].html;
    }
    try {
      const url = getCloudFrontUrl();
      // const url = "http://localhost:4100/regenerate_article/regenerate";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: html,
          type: option,
          custom_instruction: prompt,
        }),
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      let done = false;

      while (!done) {
        const { value, done: doneReading } = await reader?.read()!;
        done = doneReading;
        const chunk = decoder.decode(value, { stream: true });
        setLoading(false);
        accumulatedText += chunk;
        const converter = new showdown.Converter({
          tables: true,
          // Optional: you might want these other useful extensions too
          tasklists: true,
          strikethrough: true,
          emoji: true,
        });
        const html = converter.makeHtml(accumulatedText);
        setRegenerateText((prev) => [
          ...prev,
          { from: from, to: to, html: html },
        ]);
      }
    } catch (error) {
      console.error("Error streaming data:", error);
    } finally {
      // setLoading(false);
    }
  };

  const replaceSection = () => {
    if (!editor) return;
    const from = regenerateText[0].from;
    const content = regenerateText[regenerateText.length - 1].html;
    const plainTextLength = stripHtml(content).length;
    const to = from + plainTextLength;

    editor
      .chain()
      .focus()
      .deleteRange({
        from: regenerateText[0].from,
        to: regenerateText[regenerateText.length - 1].to,
      })
      .insertContentAt(regenerateText[0].from, content)
      .setTextSelection({
        from: from,
        to: to,
      })
      .setHighlight("#D3D3D3")
      .run();

    // Remove highlight after 2 seconds
    setTimeout(() => {
      editor
        .chain()
        .setTextSelection({ from: from, to: to })
        .unsetHighlight()
        .run();
    }, 3000);

    setResponeDialog(false);
    setRegenerateText([]);
    setSelectedOption("");
    setFollowUpPrompt("");
    setCustomPrompt("");
    setIsOpen(false);
    onClose();
    setMarginBottom(0);
  };
  const insertBelow = () => {
    if (!editor) return;
    editor.commands.unsetHighlight();

    const to = regenerateText[regenerateText.length - 1].to;
    const content = regenerateText[regenerateText.length - 1].html;
    const plainTextLength = stripHtml(content).length;
    const newTo = to + plainTextLength;

    // First insert the content
    editor.chain().focus().insertContentAt(to, content).run();

    // Then set selection and highlight in a separate command
    setTimeout(() => {
      editor
        .chain()
        .focus()
        .setTextSelection({
          from: to,
          to: newTo,
        })
        .setHighlight("#D3D3D3")
        .run();

      // Remove highlight after 3 seconds
      setTimeout(() => {
        editor
          .chain()
          .setTextSelection({
            from: to,
            to: newTo,
          })
          .unsetHighlight()
          .run();
      }, 3000);
    }, 0);

    setResponeDialog(false);
    setRegenerateText([]);
    setSelectedOption("");
    setFollowUpPrompt("");
    setCustomPrompt("");
    setIsOpen(false);
    onClose();
    setMarginBottom(0);
  };
  const regenerate = () => {
    setResponeDialog(true);
    setIsOpen(false);
    setRegenerateText([]);
    if (selectedOption === "Custom") {
      handleClick("Custom");
    } else {
      handleClick(selectedOption);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      // Replace 'Rewrite' with your desired default option or logic to determine the option
      handleClick("Custom");
    }
  };
  // console.log("selectedText", selectedText.length);

  // This is a simplified approach that just makes the dropdown stick to the selected text
  useEffect(() => {
    // Keep position tracking active when either dropdown is open OR response dialog is showing
    if ((!isOpen && !responeDialog) || !scrollContainerRef?.current || !editor) return;

    const container = scrollContainerRef.current;
    let animationFrameId = null;

    // The ArticleEditor component will handle scrolling

    // Function to update dropdown position based on selection
    const updateDropdownPosition = () => {
      // Skip if no selection but continue animation frame
      if (!selectedText || selectedText.length === 0 || !dropdownRef.current) {
        animationFrameId = requestAnimationFrame(updateDropdownPosition);
        return;
      }

      try {
        // Get the selection range
        const from = selectedText[0].from;
        const to = selectedText[selectedText.length - 1].to;

        // Validate positions are within document bounds
        const docSize = editor.state.doc.content.size;
        if (from < 0 || to < 0 || from >= docSize || to >= docSize || from > to) {
          animationFrameId = requestAnimationFrame(updateDropdownPosition);
          return;
        }

        // Get the coordinates from the editor
        const fromCoords = editor.view.coordsAtPos(from);
        const toCoords = editor.view.coordsAtPos(to);

        // Get the container's position
        const containerRect = container.getBoundingClientRect();

        // Find the start of the last line in the selection
        let lastLineStartPos = from;
        const selectionText = editor.state.doc.textBetween(from, to);
        const lastNewlineIndex = selectionText.lastIndexOf("\n");

        if (lastNewlineIndex !== -1) {
          // Found a newline, position after it (first character of last line)
          lastLineStartPos = from + lastNewlineIndex + 1;
        }

        // Get coordinates for the start of the last line
        const lastLineCoords = editor.view.coordsAtPos(lastLineStartPos);

        // Calculate available space below the selection
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - toCoords.bottom;

        // Height of the dropdown (approximate)
        const dropdownHeight = 150; // Adjust based on your dropdown's actual height

        // Check if there's enough space below
        const positionAbove = spaceBelow < dropdownHeight;

        let top, left;

        if (positionAbove) {
          // Position above the selection
          top =
            fromCoords.top -
            containerRect.top +
            container.scrollTop -
            dropdownHeight -
            50;
        } else {
          // Position below the selection (original behavior)
          top = toCoords.bottom - containerRect.top + container.scrollTop;
        }

        left = lastLineCoords.left - containerRect.left;

        // Update the dropdown style directly
        dropdownRef.current.style.top = `${top}px`;
        dropdownRef.current.style.left = `${left}px`;
      } catch (error) {
        console.error("Error updating dropdown position:", error);
      }

      // Continue the animation loop
      animationFrameId = requestAnimationFrame(updateDropdownPosition);
    };

    // Start the animation loop
    animationFrameId = requestAnimationFrame(updateDropdownPosition);

    // Cleanup
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isOpen, responeDialog, editor, selectedText, scrollContainerRef]);

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 55,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999,
          pointerEvents: "none",
        }}
      >
        <div
          className="regenerateDropdown"
          style={{
            position: "absolute",
            top: `${startCord}px`,
            left: `${endCord}px`,
            zIndex: 1001,
            pointerEvents: "auto",
          }}
          ref={dropdownRef}
        >
          {isOpen && (
            <Box sx={{ mt: "10px", zIndex: 1000 }}>
              <Card
                sx={{
                  zIndex: 1000,
                  position: "absolute",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px",
                  border: "1.5px solid #eee",
                  padding: "0rem 0.8rem",
                  width: "35rem",
                  height: "3rem",
                  backgroundColor: "#fff",
                }}
              >
                <TextField
                  inputRef={inputRef}
                  placeholder="Tell AI to..."
                  variant="standard"
                  fullWidth
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{
                    height: "0.5rem",
                    "& .MuiInput-underline:before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:hover:before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-underline:focus:before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInputBase-input": {
                      mt: "0.6rem",
                      marginLeft: "0.5rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <AutoAwesomeIcon
                        sx={{
                          fontSize: "1.2rem",
                          color: "#121828 !important",
                          mt: "0.8rem",
                        }}
                      />
                    ),
                  }}
                />
              </Card>
            </Box>
          )}
          {isOpen && (
            <Box sx={{ mt: "4rem" }}>
              <Card
                sx={{
                  zIndex: 1000,
                  position: "absolute",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px",
                  border: "1.5px solid #eee",
                  padding: "0rem 0.2rem",
                  width: "16rem",
                  backgroundColor: "#fff",
                }}
              >
                <Box sx={{ maxHeight: "15rem", overflowY: "auto" }}>
                  {Object.keys(allOptions).map((option) => (
                    <Box key={option} display="flex" sx={{ p: "4px" }}>
                      {allOptions[option].icon}
                      <Typography
                        color="#121828"
                        fontWeight="500"
                        sx={{
                          fontSize: "14px",
                          fontFamily: "Inter",
                          textAlign: "left",
                          cursor: "pointer",
                        }}
                        onClick={(event) => handleClick(option)}
                      >
                        {option}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Box>
          )}

          {responeDialog && (
            <Box sx={{ position: "absolute", mt: "10px" }}>
              <Card
                sx={{
                  zIndex: 1000,
                  position: "absolute",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px",
                  border: "1.5px solid #eee",
                  padding: "0rem 0.8rem",
                  width: "35rem",
                  minHeight: "15rem",
                  maxHeight: "calc(100vh - 200px)",
                  backgroundColor: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "visible",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "center",
                    marginTop: "0.5rem",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "400",
                      fontFamily: "Inter",
                      paddingRight: "1rem",
                      paddingTop: "0.5rem",
                      color: "#121828",
                      borderRadius: "12px",
                      border: "1px solid #eee",
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                      padding: "0.5rem 1rem",
                      backgroundColor: "#E6E8F0",
                    }}
                  >
                    {selectedOption === "Custom" && !followUpPrompt
                      ? customPrompt
                      : followUpPrompt
                      ? followUpPrompt
                      : selectedOption}
                  </Typography>
                </Box>
                <Divider sx={{ mt: "0.5rem" }} />
                <Box
                  sx={{
                    flex: 1,
                    minHeight: "6rem",
                    maxHeight: "15rem",
                    overflowY: "auto",
                    mt: "0.5rem",
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: "2.5rem",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontFamily: "Inter",
                        padding: "0.5rem 0rem",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: regenerateText[regenerateText.length - 1].html,
                      }}
                    />
                  )}
                </Box>
                {responeDialog && !loading && (
                  <Box sx={{ mb: "0.5rem", display: "flex" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        width: "10rem",
                        fontSize: "14px",
                        fontWeight: "500",
                        borderRadius: "4px",
                        padding: "0.5rem 1rem",
                        height: "2rem",
                      }}
                      onClick={replaceSection}
                    >
                      Replace Section
                    </Button>
                    <Button
                      variant="text"
                      sx={{
                        border: "0 !important",
                        height: "2rem",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#121828",
                        fontFamily: "Inter",
                        fontWeight: "500",
                        padding: "0.5rem 1rem",
                      }}
                      onClick={insertBelow}
                    >
                      <EditPencil
                        sx={{
                          fontSize: "1.4rem",
                          color: "#121828",
                          mt: "0.5rem",
                        }}
                      />
                      Insert below
                    </Button>
                    <Button
                      variant="text"
                      sx={{
                        border: "0 !important",
                        height: "2rem",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#121828",
                        fontFamily: "Inter",
                        fontWeight: "500",
                        padding: "0.5rem 1rem",
                      }}
                      onClick={regenerate}
                    >
                      <Refresh
                        sx={{
                          fontSize: "1.4rem",
                          color: "#121828",
                          mt: "0.5rem",
                        }}
                      />
                      Regenerate
                    </Button>
                    <Button
                      variant="text"
                      sx={{
                        border: "0 !important",
                        height: "2rem",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#121828",
                        fontFamily: "Inter",
                        fontWeight: "500",
                        padding: "0.5rem 1rem",
                      }}
                      onClick={() => {
                        onClose();
                      }}
                    >
                      <CloseIcon
                        sx={{ fontSize: "1.4rem", color: "#65748B" }}
                      />
                      Close
                    </Button>
                  </Box>
                )}
                {responeDialog && !loading && (
                  <Box
                    sx={{
                      borderTop: "1px solid #eee",
                      paddingTop: "0.5rem",
                      mb: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      height: "3rem",
                    }}
                  >
                    <TextField
                      placeholder="Tell AI what to do next..."
                      variant="standard"
                      fullWidth
                      value={followUpPrompt}
                      onChange={(e) => setFollowUpPrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      sx={{
                        "& .MuiInput-underline:before": {
                          borderBottom: "none !important",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottom: "none !important",
                        },
                        "& .MuiInput-underline:hover:before": {
                          borderBottom: "none !important",
                        },
                        "& .MuiInput-underline:focus:before": {
                          borderBottom: "none !important",
                        },
                        "& .MuiInputBase-input": {
                          marginLeft: "0.5rem",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <AutoAwesomeIcon
                            sx={{
                              fontSize: "1.2rem",
                              color: "#121828 !important",
                            }}
                          />
                        ),
                      }}
                    />
                  </Box>
                )}
              </Card>
            </Box>
          )}
        </div>
      </div>
    </>
  );
};
